import { api } from './client';

// GitHub Types
export interface GitHubConnectionStatus {
  connected: boolean;
  username?: string;
  avatarUrl?: string;
  connectedAt?: string;
}

export interface GitHubConnectResponse {
  authorizationUrl: string;
  state: string;
}

export interface GitHubRepo {
  fullName: string;
  name: string;
  htmlUrl: string;
  defaultBranch: string;
  isPrivate: boolean;
  description: string;
}

export interface LinkedRepository {
  fullName: string;
  url: string;
  defaultBranch: string;
  isPrivate: boolean;
  lastSyncedAt?: string;
  autoSync: boolean;
}

export interface ProjectGitHubInfo {
  linked: boolean;
  repository?: LinkedRepository;
  syncStatus?: string;
}

export interface GitStatus {
  status: string;
  aheadBy: number;
  behindBy: number;
  hasUncommittedChanges: boolean;
  uncommittedFiles: string[];
}

export interface GitCommit {
  sha: string;
  shortSha: string;
  message: string;
  author: string;
  authorEmail: string;
  date: string;
}

export interface PushResult {
  success: boolean;
  commitSha: string;
}

export interface PullResult {
  success: boolean;
  updatedFiles: number;
}

export interface LinkRepositoryRequest {
  action: 'create' | 'link';
  repositoryName?: string;
  repositoryFullName?: string;
  isPrivate?: boolean;
}

// GitHub Auth API
export const githubAuthApi = {
  // Get connection status
  getStatus: () => api.get<GitHubConnectionStatus>('/api/auth/github/status'),

  // Get OAuth connect URL
  getConnectUrl: () => api.get<GitHubConnectResponse>('/api/auth/github/connect'),

  // Complete connection after OAuth callback
  completeConnection: (code: string, state?: string) =>
    api.post<GitHubConnectionStatus>('/api/auth/github/complete', { code, state }),

  // Disconnect GitHub account
  disconnect: () => api.post('/api/auth/github/disconnect'),

  // List user's GitHub repos
  listRepos: (page = 1, perPage = 30) =>
    api.get<GitHubRepo[]>(`/api/auth/github/repos?page=${page}&perPage=${perPage}`),
};

// GitHub Repository API (project-specific)
export const githubRepoApi = {
  // Get project's GitHub info
  getInfo: (projectId: string) =>
    api.get<ProjectGitHubInfo>(`/api/projects/${projectId}/github`),

  // Link a repository
  link: (projectId: string, request: LinkRepositoryRequest) =>
    api.post<ProjectGitHubInfo>(`/api/projects/${projectId}/github/link`, request),

  // Unlink repository
  unlink: (projectId: string) =>
    api.post(`/api/projects/${projectId}/github/unlink`),

  // Push changes
  push: (projectId: string, message?: string) =>
    api.post<PushResult>(`/api/projects/${projectId}/github/push`, { message }),

  // Pull changes
  pull: (projectId: string) =>
    api.post<PullResult>(`/api/projects/${projectId}/github/pull`),

  // Get git status
  getStatus: (projectId: string) =>
    api.get<GitStatus>(`/api/projects/${projectId}/github/status`),

  // Get recent commits
  getCommits: (projectId: string, count = 10) =>
    api.get<GitCommit[]>(`/api/projects/${projectId}/github/commits?count=${count}`),
};
